import { useCallback, useEffect, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import { Navigate, useParams } from "react-router-dom"
import HomeLayout from "modules/home-layout"
import PreferenceContext from "hooks/preferences"
import { useAccountId, useAccountAlias, useIsMultiDashboardsEnabled } from "resources/account"
import Loading from "components/loading"
import { AiContext } from "connected-components/ai-teammate/context/ai-context"
import { useClearDashboardPreferences } from "./hooks/use-clear-dashboard-preferences"
import { DashboardIdProvider, useDashboardId, DEFAULT_DASHBOARD_ID } from "./context/dashboard-id"
import { getDashboardPreferenceKey, getDashboardPreferenceFallbackKey } from "./helpers/preference-keys"
import { useDashboardPreferencesSync } from "./hooks/use-dashboard-preferences-sync"
import { useModelId } from "./hooks/use-model-id"
import { useModelSync } from "./hooks/use-model-sync"
import { useAppSelector } from "hooks/redux"
import { DEFAULT_CORE_STATE } from "./store/core-slice"
import TradeSyncEffect from "./components/trade-sync-effect"
import { useDashboardRouteResolution } from "./hooks/use-dashboard-route-resolution"
import { useInstanceWorkspaceLoader } from "./hooks/use-instance-workspace-loader"
import { useWorkspaceSync } from "./hooks/use-workspace-sync"
import { useLazyMigration } from "./hooks/use-lazy-migration"
import { InstanceWorkspaceProvider, useInstanceWorkspaceContext } from "./context/instance-workspace-context"

// Runs the single workspace writer inside the InstanceWorkspaceProvider so it can read the live
// Dockview arrangement getter (populated by the renderer) at flush time. No-ops when flag off.
function WorkspaceSyncRunner({ dashboardId, canPersist }) {
  const context = useInstanceWorkspaceContext()
  // The engine clears its getter on unmount BEFORE this writer's flush runs, so cache the last live
  // arrangement — else the unmount flush falls back to the seeded one and loses an in-session drag.
  const lastLiveArrangementRef = useRef(null)
  const getArrangement = useCallback(() => {
    const live = context?.arrangementGetterRef.current?.()
    if (live) {
      lastLiveArrangementRef.current = live
      return live
    }
    // Getter gone: prefer the last live arrangement we captured (e.g. a drag), and fall back to the
    // seed only when the engine never wired (no live ever seen) — never clobber a real change.
    return lastLiveArrangementRef.current ?? context?.instanceArrangement ?? null
  }, [context])
  const requestSave = useWorkspaceSync(dashboardId, getArrangement, canPersist)
  // Expose the save trigger so the layout engine can flush a Dockview arrangement change (which
  // doesn't dispatch to Redux) through the workspace writer.
  useEffect(() => {
    if (!context) return undefined
    context.requestSaveRef.current = requestSave
    return () => {
      context.requestSaveRef.current = null
    }
  }, [context, requestSave])
  return null
}

WorkspaceSyncRunner.propTypes = {
  dashboardId: PropTypes.string.isRequired,
  canPersist: PropTypes.bool.isRequired,
}

// Instance-mode gate: seeds the slices from layout + saved workspace BEFORE the sync hooks (in
// DashboardWithContext's subtree) mount, so they treat the seed as authoritative. Renders <Loading/>
// until the seed completes so render 0 never flashes defaults. Flag-off short-circuits.
function InstanceWorkspaceGate({ children }) {
  const dashboardId = useDashboardId()
  const isInstanceMode = useIsMultiDashboardsEnabled()
  const { isInitialized, instanceArrangement, loadSucceeded } = useInstanceWorkspaceLoader(dashboardId)

  if (!isInstanceMode) {
    return children
  }

  if (!isInitialized) {
    return <Loading />
  }

  return (
    <InstanceWorkspaceProvider instanceArrangement={instanceArrangement}>
      <WorkspaceSyncRunner dashboardId={dashboardId} canPersist={loadSucceeded} />
      {children}
    </InstanceWorkspaceProvider>
  )
}

InstanceWorkspaceGate.propTypes = {
  children: PropTypes.node,
}

// Lazy migration (APP-3155): on first flag-on load of a legacy dashboard, import its hydrated state
// as a layout + instance and replace-navigate to it. Mounts inside DashboardWithContext (after
// isStoreLoaded) so the migration reads fully-hydrated slices; flag-off / real-instance ids "skip".
function LazyMigrationGate({ dashboardId, children }) {
  const accountAlias = useAccountAlias()
  const result = useLazyMigration(dashboardId)

  if (result.status === "pending") {
    return <Loading />
  }

  // Routes are account-scoped, so the alias prefix is required (else 404). Route to the slug; the
  // resolver maps it back to the canonical instanceId.
  if (result.status === "migrated" && result.instanceId !== dashboardId) {
    return <Navigate to={`/${accountAlias}/dashboard/${result.slug}`} replace />
  }

  return children
}

LazyMigrationGate.propTypes = {
  dashboardId: PropTypes.string.isRequired,
  children: PropTypes.node,
}

function DashboardWithContext({ children }) {
  const dashboardId = useDashboardId()
  const isStoreLoaded = useDashboardPreferencesSync()
  const modelId = useModelId()

  const core = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)

  // One model-sync per dashboard — keep global home layout in sync with modelId.
  useModelSync(modelId)

  const aiContextValue = useMemo(() => ({
    modelId,
    instrument: core.instrument,
    base: core.base,
    startDate: core.startDate,
    endDate: core.endDate,
    isPreferencesLoaded: isStoreLoaded,
    canPinArtifacts: true,
  }), [
    modelId,
    core.instrument,
    core.base,
    core.startDate,
    core.endDate,
    isStoreLoaded,
  ])

  if (!isStoreLoaded) {
    return <Loading />
  }

  return (
    <AiContext.Provider value={aiContextValue}>
      <TradeSyncEffect />
      {children}
    </AiContext.Provider>
  )
}

export default function DashboardHomeLayout() {
  const accountId = useAccountId()
  const accountAlias = useAccountAlias()
  const { dashboardId: routeDashboardId } = useParams()

  // Resolve the effective id BEFORE the provider so the remount key stays stable for the sync hooks.
  const resolution = useDashboardRouteResolution(routeDashboardId)
  const dashboardId = resolution.kind === "resolved" ? resolution.dashboardId : DEFAULT_DASHBOARD_ID

  // Gate the clear on a RESOLVED id: ungated, ?clearPreferences would race the instances fetch and
  // wipe the DEFAULT-fallback dashboard instead of the routed one.
  const { isClearing: isPreferencesClearing } = useClearDashboardPreferences(
    dashboardId,
    resolution.kind === "resolved",
  )

  const preferencesProps = useMemo(() => ({
    accountId,
    key: getDashboardPreferenceKey(dashboardId),
    fallbackKey: getDashboardPreferenceFallbackKey(dashboardId),
    type: "dashboard",
  }), [accountId, dashboardId])

  if (resolution.kind === "loading" || isPreferencesClearing) {
    return <Loading />
  }

  // Unknown / foreign / deleted id: replace-navigate to the account's bare /dashboard (toast fired
  // in the hook; alias prefix required since routes are scoped under /:accountValue).
  if (resolution.kind === "redirect") {
    return <Navigate to={`/${accountAlias}/dashboard`} replace />
  }

  // key forces a remount when the route param changes: React Router keeps the element mounted
  // across param-only navigations, but the preference sync hooks require a stable dashboardId
  // for their lifetime (one-shot load refs in usePreferences).
  return (
    <PreferenceContext.Provider key={dashboardId} value={preferencesProps}>
      <DashboardIdProvider value={dashboardId}>
        <InstanceWorkspaceGate>
          <DashboardWithContext>
            <LazyMigrationGate dashboardId={dashboardId}>
              <HomeLayout />
            </LazyMigrationGate>
          </DashboardWithContext>
        </InstanceWorkspaceGate>
      </DashboardIdProvider>
    </PreferenceContext.Provider>
  )
}
